import { DUAL_BACKEND_GRAPHICS_REQUIREMENTS } from '@/graphics/GraphicsRequirements'
import type { EngineContext } from '@/runtime/EngineContext'
import type { Disposable } from '@/shared/types/core'
import type { Plugin } from '@/shared/types/plugin'
import type { RenderInvalidationService } from '@/shared/types/services'
import type { EngineFrameContext } from '@/shared/types/view'
import { AnimationServiceImpl } from './AnimationService'
import { createCameraBindingResolver } from './cameraBinding'

export class AnimationPlugin implements Plugin {
  readonly name = 'animation'
  readonly graphicsRequirements = DUAL_BACKEND_GRAPHICS_REQUIREMENTS
  readonly priority = 18
  readonly provides = ['AnimationService'] as const

  private context: EngineContext | null = null
  private service: AnimationServiceImpl | null = null
  private cameraBindingDisposable: Disposable | null = null
  private activityDisposable: Disposable | null = null

  onInit(context: EngineContext): void {
    this.context = context
    const invalidation = context.hasService('RenderInvalidationService')
      ? (context.getService('RenderInvalidationService') as RenderInvalidationService)
      : null
    this.service = new AnimationServiceImpl({
      onVisualChange: (reason, detail) => invalidation?.invalidate(reason, { detail }),
    })
    context.registerService('AnimationService', this.service)
    this.activityDisposable =
      invalidation?.registerActivitySource({
        id: 'animation',
        getRenderActivity: () =>
          this.service?.hasPlayingPlayers()
            ? { needsNextFrame: true, reasons: ['animation.playing'] }
            : { needsNextFrame: false },
      }) ?? null
  }

  onStart(): void {
    if (!this.context || !this.service) {
      return
    }

    if (this.context.hasService('CameraBindingHost')) {
      const cameraHost = this.context.getService('CameraBindingHost')
      this.cameraBindingDisposable = this.service.registerBinding(
        'camera',
        createCameraBindingResolver(cameraHost)
      )
    }
  }

  onUpdateFrame(frame: EngineFrameContext): void {
    this.service?.update(frame.delta)
  }

  onDestroy(): void {
    this.activityDisposable?.dispose()
    this.activityDisposable = null
    this.cameraBindingDisposable?.dispose()
    this.cameraBindingDisposable = null
    this.service?.dispose()
    this.service = null
    this.context = null
  }
}
